import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import Navigation from '../components/Navigation'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'

export default function PaymentCancelPage() {
  const { user, loading } = useAuth()
  const navigate = useNavigate()
  const { showToast } = useToast()
  const hasShownToast = useRef(false)

  useEffect(() => {
    if (!loading && !user) {
      navigate('/')
      return
    }

    if (!loading && !hasShownToast.current) {
      hasShownToast.current = true
      showToast('info', 'Payment cancelled', 'No charges were made')
    }
  }, [user, loading, navigate, showToast])

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <Navigation />

      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center">
          {/* Cancel Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-24 h-24 bg-yellow-100 dark:bg-yellow-900/40 rounded-full flex items-center justify-center border-4 border-yellow-300 dark:border-yellow-700">
              <svg className="w-14 h-14 text-yellow-600 dark:text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
              </svg>
            </div>
          </div>

          {/* Cancel Message */}
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Payment Cancelled
          </h1>

          <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
            Your payment has been cancelled. No charges were made to your account.
          </p>

          {/* Info Box */}
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6 mb-8 text-left">
            <h3 className="font-bold text-yellow-900 dark:text-yellow-100 mb-3">What happened?</h3>
            <p className="text-sm text-yellow-900 dark:text-yellow-200 mb-4">
              You successfully returned from the payment page without completing the transaction. This means:
            </p>
            <ul className="space-y-2 text-sm text-yellow-900 dark:text-yellow-200">
              <li>✓ No charges were applied to your card</li>
              <li>✓ Your account remains unchanged</li>
              <li>✓ You can try again anytime</li>
              <li>✓ Your payment information is secure</li>
            </ul>
          </div>

          {/* Questions Box */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6 mb-8 text-left">
            <h3 className="font-bold text-blue-900 dark:text-blue-100 mb-3">Need help?</h3>
            <ul className="space-y-2 text-sm text-blue-900 dark:text-blue-200">
              <li>💳 Having payment issues? <a href="mailto:support@contentindexer.com" className="font-semibold hover:underline">Contact support</a></li>
              <li>❓ Questions about pricing? <a href="/#pricing" className="font-semibold hover:underline">View pricing details</a></li>
              <li>📖 Need documentation? <a href="/#docs" className="font-semibold hover:underline">Check our docs</a></li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 justify-center flex-wrap">
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors"
            >
              Back to Dashboard
            </button>
            <button
              onClick={() => navigate('/')}
              className="border-2 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
